import { useQuery } from "@tanstack/react-query";
import { fetchAllPages } from "../api/client";
import { getMyFeed } from "../api/tmTasks";
import { getWorkerUrl } from "../lib/session";
import { useAuth } from "../lib/auth/useAuth";
import { useSessionsOpen } from "./useSessionsOpen";

/** Item unifié « Mes tâches » — agrégation frontend TM natif + TAWA
 * (arbitrage 07/07 : le backend /tasks/me n'agrège pas encore ; ce hook isole
 * le choix pour une bascule backend future sans toucher les écrans).
 * Les tâches terrain (LTE) sont EXCLUES : GET /lte/tasks est tenant-wide sans
 * filtre d'assignation côté backend — les inclure montrerait à chaque worker
 * les tâches de tout le tenant. Réintégrer quand le backend exposera un filtre
 * « assigné à moi ». */
export interface MyTaskItem {
  origin: "native" | "terrain" | "temps";
  id: string;
  title: string;
  status: string;
  priority?: string;
  due?: string;
  contextLabel?: string;
}

const TM_CLOSED = ["done", "cancelled", "archived"];

export function useMyTasks() {
  const feed = useQuery({
    queryKey: ["tasks", "me"],
    // Draine toutes les pages : le filtre source === "native" est client-side,
    // une seule page (50, tri newest-first) ferait disparaître les tâches
    // natives dès que 50+ items répliqués PM/CRM sont plus récents.
    queryFn: () => fetchAllPages((cursor) => getMyFeed(cursor)),
    staleTime: 30_000,
  });
  const sessions = useSessionsOpen();
  const { user } = useAuth();

  let workerUrl: string | null = null;
  try {
    workerUrl = getWorkerUrl(user);
  } catch {
    // Non authentifié / claim absent : pas de ligne « temps », pas d'erreur écran.
  }
  const items: MyTaskItem[] = [];

  for (const it of feed.data?.items ?? []) {
    // Filtre arbitrage 02/07 : les work items PM/CRM répliqués ne sortent pas dans Chronos.
    if (it.source !== "native" || TM_CLOSED.includes(it.status)) continue;
    items.push({
      origin: "native",
      id: it.id,
      title: it.title,
      status: it.status,
      priority: it.priority,
      due: it.due_date,
      contextLabel: it.context_label,
    });
  }

  const mySession = (sessions.data?.sessions ?? []).find(
    (s) => s.worker_url === workerUrl,
  );
  if (mySession) {
    items.push({
      origin: "temps",
      id: `session-${mySession.worker_url}`,
      title: "Session en cours",
      status: "active",
      due: undefined,
      contextLabel: new Date(mySession.opened_at).toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    });
  }

  items.sort((a, b) => (a.due ?? "9999").localeCompare(b.due ?? "9999"));

  return {
    items,
    isLoading: feed.isLoading,
    isError: feed.isError,
  };
}
